import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';

const CompanyAutocomplete = ({ 
  value, 
  onChange, 
  label = "Şirket", 
  placeholder = "Şirket ara...",
  error = false,
  errorMessage = ""
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const wrapperRef = useRef(null);
  
  // Fetch companies when component mounts
  useEffect(() => {
    fetchCompanies();
  }, []);

  // Initialize the input value based on the selected value
  useEffect(() => {
    if (value) {
      const selectedCompany = companies.find(company => company.id === value);
      if (selectedCompany) {
        setInputValue(selectedCompany.company_name);
      } else {
        setInputValue('');
      }
    } else {
      setInputValue('');
    }
  }, [value, companies]);

  // Filter companies based on input value
  useEffect(() => {
    if (companies && companies.length > 0) {
      const filtered = companies.filter(company => {
        return company.company_name.toLowerCase().includes(inputValue.toLowerCase());
      });
      setFilteredCompanies(filtered);
    } else {
      setFilteredCompanies([]);
    }
  }, [inputValue, companies]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_type', 'company')
        .order('company_name', { ascending: true });

      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setIsOpen(true);
    
    // If input is cleared, also clear the selected value
    if (!e.target.value) {
      onChange('');
    }
  };

  const handleCompanyClick = (company) => {
    onChange(company.id);
    setInputValue(company.company_name);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {error && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          autoComplete="new-password"
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? 'border-red-500' : 'border-gray-300'
          }`}
        />
        {isOpen && filteredCompanies.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {filteredCompanies.map((company) => (
              <div
                key={company.id}
                onClick={() => handleCompanyClick(company)}
                className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
              >
                <div className="font-medium">{company.company_name}</div>
                {company.email && (
                  <div className="text-sm text-gray-500">
                    {company.email}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && errorMessage && (
        <p className="error-message text-red-500 text-xs mt-1">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default CompanyAutocomplete;
